import forEach from 'lodash/forEach';
import times from 'lodash/times';
import moment from 'moment';
import axios from 'axios';
import APIUtils, {APIRegistry} from '@spothero/utils/api';
import Config from 'config';
import SearchUtils from 'search/search-utils';
import SearchTracking from 'utils/search-tracking';
import SegmentUtils from 'utils/segment';
import {retrieveTravelDistances} from 'utils/travel-distance';

export const SPOTS_GET_RADIAL = 'SPOTS_GET_RADIAL';
export const SPOTS_GET_TILED = 'SPOTS_GET_TILED';
export const SPOTS_GET_AIRPORT = 'SPOTS_GET_AIRPORT';
export const SPOTS_GET_MOBILE = 'SPOTS_GET_MOBILE';
export const SPOTS_GET_MOBILE_PAGINATION = 'SPOTS_GET_MOBILE_PAGINATION';
export const SPOTS_GET_TRAVEL_DISTANCES = 'SPOTS_GET_TRAVEL_DISTANCES';
export const SPOTS_RADIAL_INCREMENT = 'SPOTS_RADIAL_INCREMENT';
export const SPOTS_TILED_INCREMENT = 'SPOTS_TILED_INCREMENT';
export const SPOTS_CANCEL_REQUESTS = 'SPOTS_CANCEL_REQUESTS';
export const SPOTS_SET_SPOT_SELECTED_RATE = 'SPOTS_SET_SPOT_SELECTED_RATE';

const cancelSources = [];
const cancelCurrentRequests = message => {
    while (cancelSources.length) {
        cancelSources[0].cancel(message);
        cancelSources.shift();
    }
};
const checkForErrorsInResponse = args => {
    const errorData = [];

    forEach(args, responseData => {
        if (responseData.status && responseData.status >= 400) {
            const errors = responseData.data.data.errors;

            if (errors) {
                errorData.push(...errors);
            }
        }
    });

    return errorData;
};

export const cancelSpotRequests = () => {
    cancelCurrentRequests();

    return {
        type: SPOTS_CANCEL_REQUESTS
    };
};

export const getTravelDistances = dispatch => {
    return dispatch({
        type: SPOTS_GET_TRAVEL_DISTANCES,
        payload: APIUtils.all(retrieveTravelDistances())
    });
};

export const getSpotsRadial = ({dateTimeChanged = false, autoZoom = false}) => {
    cancelCurrentRequests();

    return (dispatch, getState) => {
        const searchAPIUtils = APIRegistry.get('ratesSearch');
        const prevState = getState();
        const destination = prevState.destination.data;
        const prevSearchState = prevState.search.data;
        const searchRequestState = prevState.searchRequest;
        const mapBounds = prevSearchState.searchMapInstance.getBounds();
        const searchState = SearchUtils.prepareRequest({
            requestType: 'radial',
            bounds: mapBounds,
            searchState: prevSearchState,
            searchRequestState
        });
        const {
            requestIncrements,
            radialDistances,
            geolocated,
            isDefaultTimes
        } = searchState;
        const eventId = searchState.params.eid;
        const url = (eventId)
            ? `events/${eventId}/rates/`
            : 'facilities/rates/';
        const requests = [];
        const destinationTitle = (destination)
            ? destination.title
            : '';
        const segmentId = SegmentUtils.getAnonymousId();
        let trackingAction = (eventId) ? 'EVENT' : 'SEARCH';

        if (geolocated) {
            trackingAction = 'FIND_ME';
        }

        if (dateTimeChanged) {
            trackingAction = 'DATE_CHANGED';
        }

        if (autoZoom) {
            trackingAction = 'AUTO_ZOOM';
        }

        SearchTracking.updateAction(trackingAction);

        const params = SearchTracking.assignParams({
            params: searchState.params,
            mapBounds,
            isDefaultTimes,
            activeAmenities: prevState.filters.activeAmenities,
            searchText: searchState.params.search_string || destinationTitle
        });

        if (params.monthly) {
            params.monthly_start_date = moment(params.starts).format(Config.urlDateFormat); // eslint-disable-line camelcase

            delete params.starts;
            delete params.ends;
        } else {
            delete params.monthly_start_date;
        }

        params.segment_id = segmentId; // eslint-disable-line camelcase
        params.mixpanel_id = segmentId; // eslint-disable-line camelcase

        times(requestIncrements, () => {
            const data = {
                ...params,
                distance__gt: Number(radialDistances[0]), // eslint-disable-line camelcase
                distance__lt: Number(radialDistances[1]) // eslint-disable-line camelcase
            };
            const cancelSource = axios.CancelToken.source();
            const config = {
                cancelToken: cancelSource.token
            };

            radialDistances.shift();
            cancelSources.push(cancelSource);

            requests.push(
                searchAPIUtils
                    .get(url, data, config)
                    .then(response => {
                        const responseData = response.data.data.results;

                        dispatch({
                            type: SPOTS_RADIAL_INCREMENT,
                            payload: {
                                results: responseData,
                                searchRequestState,
                                timezone: prevState.city.data.timezone
                            }
                        });

                        return responseData;
                    })
                    .catch(error => {
                        return error;
                    })
            );
        });

        return dispatch({
            type: SPOTS_GET_RADIAL,
            payload: searchAPIUtils
                .all(requests)
                .then(searchAPIUtils.spread((...args) => {
                    const errors = checkForErrorsInResponse(args);

                    if (errors.length) {
                        return Promise.reject(errors);
                    } else {
                        getTravelDistances(dispatch)
                            .catch(error => {
                                // silently catch this error to ignore errors being logged
                            });
                    }
                }))
                .catch(error => {
                    return Promise.reject(error);
                }),
            meta: {
                numRequests: requestIncrements
            }
        });
    };
};

export const getSpotsTiled = bounds => {
    cancelCurrentRequests();

    return (dispatch, getState) => {
        const searchAPIUtils = APIRegistry.get('ratesSearch');
        const prevState = getState();
        const destination = prevState.destination.data;
        const prevSearchState = prevState.search.data;
        const searchRequestState = prevState.searchRequest;
        const mapBounds = bounds || prevSearchState.searchMapInstance.getBounds();
        const searchState = SearchUtils.prepareRequest({
            requestType: 'tiled',
            bounds: mapBounds,
            searchState: prevSearchState,
            searchRequestState
        });
        const {
            map,
            tiles,
            requestIncrements,
            isDefaultTimes
        } = searchState;
        const eventId = searchState.params.eid;
        const requests = [];
        const url = (eventId)
            ? `events/${eventId}/rates/`
            : 'facilities/rates/';
        const searchText = (searchState.params.search_string)
            ? searchState.params.search_string
            : (destination)
                ? destination.title
                : null;
        const params = SearchTracking.assignParams({
            params: searchState.params,
            activeAmenities: prevState.filters.activeAmenities,
            mapBounds,
            isDefaultTimes,
            searchText
        });
        const segmentId = SegmentUtils.getAnonymousId();

        if (params.monthly) {
            params.monthly_start_date = moment(params.starts).format(Config.urlDateFormat); // eslint-disable-line camelcase

            delete params.starts;
            delete params.ends;
        } else {
            delete params.monthly_start_date;
        }

        params.segment_id = segmentId; // eslint-disable-line camelcase
        params.mixpanel_id = segmentId; // eslint-disable-line camelcase

        while (tiles.length) {
            const data = {
                ...params,
                ...SearchUtils.getTileData(tiles[0], map)
            };
            const cancelSource = axios.CancelToken.source();
            const config = {
                cancelToken: cancelSource.token
            };

            tiles.shift();
            cancelSources.push(cancelSource);

            requests.push(
                searchAPIUtils
                    .get(url, data, config)
                    .then(response => {
                        const responseData = response.data.data.results;

                        dispatch({
                            type: SPOTS_TILED_INCREMENT,
                            payload: {
                                results: responseData,
                                searchRequestState,
                                timezone: prevState.city.data.timezone
                            }
                        });

                        return responseData;
                    })
                    .catch(error => {
                        return error;
                    })
            );
        }

        return dispatch({
            type: SPOTS_GET_TILED,
            payload: searchAPIUtils
                .all(requests)
                .then(searchAPIUtils.spread((...args) => {
                    const errors = checkForErrorsInResponse(args);

                    if (errors.length) {
                        return Promise.reject(errors);
                    }
                    // temp disable travel distance request on all but initial spots
                    // } else {
                    //     getTravelDistances(dispatch)
                    //         .catch(error => {
                    //             // silently catch this error to ignore errors being logged
                    //         });
                    // }
                }))
                .catch(error => {
                    return Promise.reject(error);
                }),
            meta: {
                numRequests: requestIncrements
            }
        });
    };
};

export const getSpotsMobile = ({dateTimeChanged = false, limit = 15, minDistance = 0, maxDistance = 1609}) => {
    cancelCurrentRequests();

    return (dispatch, getState) => {
        const searchAPIUtils = APIRegistry.get('ratesSearch');
        const prevState = getState();
        const destination = prevState.destination.data;
        const prevSearchState = prevState.search.data;
        const searchRequestState = prevState.searchRequest;
        const searchState = SearchUtils.prepareRequest({
            requestType: 'mobile',
            bounds: null,
            searchState: prevSearchState,
            searchRequestState
        });
        const {
            isDefaultTimes
        } = searchState;
        const eventId = searchState.params.eid;
        const url = (eventId)
            ? `events/${eventId}/rates/`
            : 'facilities/rates/';
        const cancelSource = axios.CancelToken.source();
        const config = {
            cancelToken: cancelSource.token
        };
        const segmentId = SegmentUtils.getAnonymousId();
        let trackingAction = (eventId) ? 'LIST_EVENT' : 'LIST_SEARCH';

        if (dateTimeChanged) {
            trackingAction = 'LIST_DATE_CHANGED';
        }

        if (minDistance) {
            trackingAction = 'LIST_SEARCH_NEXT';
        }

        SearchTracking.updateAction(trackingAction);

        const searchText = (searchState.params.search_string)
            ? searchState.params.search_string
            : (destination)
                ? destination.title
                : null;
        const params = SearchTracking.assignParams({
            params: searchState.params,
            activeAmenities: prevState.filters.activeAmenities,
            isDefaultTimes,
            searchText
        });

        params.limit = limit;

        if (params.monthly) {
            params.monthly_start_date = moment(params.starts).format(Config.urlDateFormat); // eslint-disable-line camelcase

            delete params.starts;
            delete params.ends;
        } else {
            delete params.monthly_start_date;
        }

        params.segment_id = segmentId; // eslint-disable-line camelcase
        params.mixpanel_id = segmentId; // eslint-disable-line camelcase

        const data = {
            ...params,
            distance__gt: minDistance, // eslint-disable-line camelcase
            distance__lt: (minDistance > maxDistance) // eslint-disable-line camelcase
                ? minDistance + 1609
                : (!maxDistance || maxDistance < 1609) // ensure max distance is at least 1 mile
                    ? 1609
                    : maxDistance
        };

        cancelSources.push(cancelSource);

        return dispatch({
            type: (minDistance) ? SPOTS_GET_MOBILE_PAGINATION : SPOTS_GET_MOBILE,
            payload: searchAPIUtils
                .get(url, data, config)
                .then(response => {
                    // temp disable travel distance request on all but initial spots
                    if (!minDistance) {
                        getTravelDistances(dispatch)
                            .catch(error => {
                                // silently catch this error to ignore errors being logged
                            });
                    }

                    return {
                        results: response.data.data.results,
                        searchRequestState,
                        timezone: prevState.city.data.timezone
                    };
                })
                .catch(error => {
                    return error;
                }),
            meta: {
                isMobileRequest: true
            }
        });
    };
};

export const getSpotsAirport = () => {
    cancelCurrentRequests();

    return (dispatch, getState) => {
        const searchAPIUtils = APIRegistry.get('ratesSearch');
        const prevState = getState();
        const destination = prevState.destination.data;
        const prevSearchState = prevState.search.data;
        const searchRequestState = prevState.searchRequest;
        const searchState = SearchUtils.prepareRequest({
            requestType: 'airport',
            searchState: prevSearchState,
            searchRequestState
        });
        const {
            isDefaultTimes,
            params: searchStateParams
        } = searchState;
        const cancelSource = axios.CancelToken.source();
        const config = {
            cancelToken: cancelSource.token
        };
        const segmentId = SegmentUtils.getAnonymousId();

        SearchTracking.updateAction('LIST_SEARCH');

        const searchText = (searchStateParams.search_string)
            ? searchStateParams.search_string
            : (destination)
                ? destination.title
                : null;
        const params = SearchTracking.assignParams({
            params: searchStateParams,
            activeAmenities: prevState.filters.activeAmenities,
            isDefaultTimes,
            searchText
        });

        params.segment_id = segmentId; // eslint-disable-line camelcase
        params.mixpanel_id = segmentId; // eslint-disable-line camelcase

        // iata is required for airport facility rates search
        params.iata = destination.airport.iata_code;

        // facility_url is not included in the default facility include list
        if (params.include) {
            params.include += ',facility.facility_url';
        }

        cancelSources.push(cancelSource);

        return dispatch({
            type: SPOTS_GET_AIRPORT,
            payload: searchAPIUtils
                .get('facilities/rates/airport/', params, config)
                .then(response => {
                    getTravelDistances(dispatch)
                        .catch(error => {
                            // silently catch this error to ignore errors being logged
                        });

                    return {
                        results: response.data.data.results,
                        searchRequestState,
                        timezone: prevState.city.data.timezone
                    };
                })
                .catch(error => {
                    return {error};
                })
        });
    };
};

export const setSpotSelectedRate = spotWithNewRate => {
    return {
        type: SPOTS_SET_SPOT_SELECTED_RATE,
        payload: {
            spotWithNewRate
        }
    };
};
